import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { API } from "../../App";
import { Button } from "../../components/ui/button";
import { 
  ArrowLeft, Plus, Trash2, Edit2, Check, X, 
  Tag, Percent, Users, Calendar, Gift, Loader2,
  ToggleLeft, ToggleRight
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const LOGO_URL = "/images/singleseen_logo_transparent.png";

const AdminPromoPage = () => {
  const navigate = useNavigate();
  const token = localStorage.getItem("admin_token");
  const [promos, setPromos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingPromo, setEditingPromo] = useState(null);
  const [formData, setFormData] = useState({
    code: "",
    discount_percent: 10,
    description: "",
    is_active: true,
    for_new_users_only: true,
    max_uses: "",
    valid_until: ""
  });

  useEffect(() => {
    if (!token) {
      navigate("/admin");
      return;
    }
    fetchPromos();
  }, [token]);

  const fetchPromos = async () => {
    try {
      const response = await axios.get(`${API}/admin/promo/list`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPromos(response.data.promo_codes || []);
    } catch (err) {
      if (err.response?.status === 401 || err.response?.status === 403) {
        localStorage.removeItem("admin_token");
        navigate("/admin");
      }
      toast.error("Failed to fetch promo codes");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (editingPromo) {
        await axios.put(
          `${API}/admin/promo/${editingPromo.code}`,
          {
            ...formData,
            max_uses: formData.max_uses ? parseInt(formData.max_uses) : null,
            valid_until: formData.valid_until || null
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success("Promo code updated!");
      } else {
        await axios.post(
          `${API}/admin/promo/create`,
          {
            ...formData,
            max_uses: formData.max_uses ? parseInt(formData.max_uses) : null,
            valid_until: formData.valid_until || null
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success("Promo code created!");
      }
      
      setShowForm(false);
      setEditingPromo(null);
      resetForm();
      fetchPromos();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save promo code");
    }
  };

  const handleDelete = async (code) => {
    if (!confirm(`Delete promo code "${code}"?`)) return;
    
    try {
      await axios.delete(`${API}/admin/promo/${code}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Promo code deleted");
      fetchPromos();
    } catch (err) {
      toast.error("Failed to delete promo code");
    }
  };

  const toggleActive = async (promo) => {
    try {
      await axios.put(
        `${API}/admin/promo/${promo.code}`,
        { is_active: !promo.is_active },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(promo.is_active ? "Promo deactivated" : "Promo activated");
      fetchPromos();
    } catch (err) {
      toast.error("Failed to update promo");
    }
  };

  const startEdit = (promo) => {
    setEditingPromo(promo);
    setFormData({
      code: promo.code,
      discount_percent: promo.discount_percent,
      description: promo.description,
      is_active: promo.is_active,
      for_new_users_only: promo.for_new_users_only || false,
      max_uses: promo.max_uses || "",
      valid_until: promo.valid_until ? promo.valid_until.split("T")[0] : ""
    });
    setShowForm(true);
  };

  const resetForm = () => {
    setFormData({
      code: "",
      discount_percent: 10,
      description: "",
      is_active: true,
      for_new_users_only: true,
      max_uses: "",
      valid_until: ""
    });
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate("/admin")}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ArrowLeft className="w-5 h-5 text-gray-600" />
            </button>
            <div className="flex items-center gap-3">
              <img src={LOGO_URL} alt="SingleSeen" className="h-14" />
              <div>
                <h1 className="text-xl font-bold text-gray-900">Promo Codes</h1>
                <p className="text-sm text-gray-500">Manage promotional offers</p>
              </div>
            </div>
          </div>
          
          <Button
            onClick={() => {
              setShowForm(true);
              setEditingPromo(null);
              resetForm();
            }}
            className="bg-emerald-600 hover:bg-emerald-700 text-white"
          >
            <Plus className="w-4 h-4 mr-2" />
            New Promo
          </Button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-8">
        {/* Quick Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <div className="bg-white rounded-xl p-6 border border-gray-200">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-emerald-100 rounded-lg">
                <Tag className="w-6 h-6 text-emerald-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{promos.length}</p>
                <p className="text-sm text-gray-500">Total Promos</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-xl p-6 border border-gray-200">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-green-100 rounded-lg">
                <Check className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">
                  {promos.filter(p => p.is_active).length}
                </p>
                <p className="text-sm text-gray-500">Active Promos</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-xl p-6 border border-gray-200">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-blue-100 rounded-lg">
                <Users className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">
                  {promos.reduce((sum, p) => sum + (p.times_used || 0), 0)}
                </p>
                <p className="text-sm text-gray-500">Total Uses</p>
              </div>
            </div>
          </div>
        </div>

        {/* Promo Form Modal */}
        {showForm && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6 border-b border-gray-100">
                <div className="flex items-center justify-between">
                  <h2 className="text-xl font-semibold">
                    {editingPromo ? "Edit Promo Code" : "Create Promo Code"}
                  </h2>
                  <button
                    onClick={() => {
                      setShowForm(false);
                      setEditingPromo(null);
                    }}
                    className="p-2 hover:bg-gray-100 rounded-full"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </div>
              
              <form onSubmit={handleSubmit} className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Promo Code *
                  </label>
                  <input
                    type="text"
                    value={formData.code}
                    onChange={(e) => setFormData({...formData, code: e.target.value.toUpperCase()})}
                    placeholder="e.g., WELCOME20"
                    className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-emerald-500"
                    required
                    disabled={editingPromo}
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Discount Percentage *
                  </label>
                  <div className="relative">
                    <input
                      type="number"
                      value={formData.discount_percent}
                      onChange={(e) => setFormData({...formData, discount_percent: parseInt(e.target.value)})}
                      min="1"
                      max="100"
                      className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-emerald-500"
                      required
                    />
                    <Percent className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Description *
                  </label>
                  <input
                    type="text"
                    value={formData.description}
                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                    placeholder="e.g., Welcome offer - 20% off!"
                    className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-emerald-500"
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Max Uses (leave empty for unlimited)
                  </label>
                  <input
                    type="number"
                    value={formData.max_uses}
                    onChange={(e) => setFormData({...formData, max_uses: e.target.value})}
                    min="1"
                    placeholder="Unlimited"
                    className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-emerald-500"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Expiry Date (optional)
                  </label>
                  <input
                    type="date"
                    value={formData.valid_until}
                    onChange={(e) => setFormData({...formData, valid_until: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-emerald-500"
                  />
                </div>
                
                <div className="flex items-center justify-between py-2">
                  <span className="text-sm text-gray-700">For new users only</span>
                  <button
                    type="button"
                    onClick={() => setFormData({...formData, for_new_users_only: !formData.for_new_users_only})}
                    className={`p-1 rounded-full transition-colors ${formData.for_new_users_only ? 'bg-emerald-500' : 'bg-gray-300'}`}
                  >
                    {formData.for_new_users_only ? (
                      <ToggleRight className="w-6 h-6 text-white" />
                    ) : (
                      <ToggleLeft className="w-6 h-6 text-white" />
                    )}
                  </button>
                </div>
                
                <div className="flex items-center justify-between py-2">
                  <span className="text-sm text-gray-700">Active</span>
                  <button
                    type="button"
                    onClick={() => setFormData({...formData, is_active: !formData.is_active})}
                    className={`p-1 rounded-full transition-colors ${formData.is_active ? 'bg-emerald-500' : 'bg-gray-300'}`}
                  >
                    {formData.is_active ? (
                      <ToggleRight className="w-6 h-6 text-white" />
                    ) : (
                      <ToggleLeft className="w-6 h-6 text-white" />
                    )}
                  </button>
                </div>
                
                <div className="flex gap-3 pt-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      setShowForm(false);
                      setEditingPromo(null);
                    }}
                    className="flex-1"
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white"
                  >
                    {editingPromo ? "Update" : "Create"}
                  </Button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Promo List */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
          </div>
        ) : promos.length === 0 ? (
          <div className="bg-white rounded-xl p-12 text-center border border-gray-200">
            <Gift className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-900 mb-2">No Promo Codes Yet</h3>
            <p className="text-gray-500 mb-6">Create your first promotional offer to attract new users</p>
            <Button
              onClick={() => setShowForm(true)}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
            >
              <Plus className="w-4 h-4 mr-2" />
              Create Promo Code
            </Button>
          </div>
        ) : (
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">Code</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">Discount</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">Description</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">Uses</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">Status</th>
                  <th className="px-6 py-4 text-right text-sm font-semibold text-gray-900">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {promos.map((promo) => (
                  <tr key={promo.code} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <Tag className="w-4 h-4 text-emerald-600" />
                        <span className="font-mono font-bold text-emerald-600">{promo.code}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className="inline-flex items-center px-2.5 py-1 rounded-full text-sm font-medium bg-emerald-100 text-emerald-800">
                        {promo.discount_percent}% OFF
                      </span>
                    </td>
                    <td className="px-6 py-4 text-gray-600 max-w-xs truncate">
                      {promo.description}
                      {promo.for_new_users_only && (
                        <span className="ml-2 text-xs text-blue-600">(New users)</span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-gray-600">
                      {promo.times_used || 0}
                      {promo.max_uses && ` / ${promo.max_uses}`}
                    </td>
                    <td className="px-6 py-4">
                      <button
                        onClick={() => toggleActive(promo)}
                        className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                          promo.is_active 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-gray-100 text-gray-600'
                        }`}
                      >
                        {promo.is_active ? (
                          <>
                            <Check className="w-3 h-3 mr-1" />
                            Active
                          </>
                        ) : (
                          <>
                            <X className="w-3 h-3 mr-1" />
                            Inactive
                          </>
                        )}
                      </button>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => startEdit(promo)}
                          className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                          title="Edit"
                        >
                          <Edit2 className="w-4 h-4 text-gray-600" />
                        </button>
                        <button
                          onClick={() => handleDelete(promo.code)}
                          className="p-2 hover:bg-red-50 rounded-lg transition-colors"
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </main>
    </div>
  );
};

export default AdminPromoPage;
